import QRCode from 'qrcode';
import { useEffect, useState } from 'react';
import { eventName, vCard } from '@wr/shared';
import { useOffline } from '../offline/context.js';
import { keptMyCard, refreshMyCard, type MyCard } from '../offline/myCard.js';
import { useSignedIn } from '../session.js';
import { useToast } from './Toast.js';
import { Dialog } from './ui.js';

/**
 * My business card (prototype: openMyCard), from the name or the avatar
 * at the top of the screen: name, title, phones, email, LinkedIn and the
 * QR code a visitor scans to save the contact (a vCard, no app or network
 * needed on their side). At a show, "Met at <event>" goes on the contact.
 * Show full screen, Share, Download .vcf, and My account for the settings
 * and the card's details. It opens offline from the copy the device keeps.
 */
export function MyCardDialog({ onClose, onAccount }: { onClose(): void; onAccount(): void }) {
  const { user } = useSignedIn();
  const { store, eventId } = useOffline();
  const toast = useToast();
  const [card, setCard] = useState<MyCard>({
    firstName: user.firstName,
    lastName: user.lastName,
    email: user.email,
    jobTitle: null,
    mobile: null,
    officePhone: null,
    linkedinUrl: null,
  });
  const [event, setEvent] = useState<string | null>(null);
  const [metAt, setMetAt] = useState(true);
  const [qr, setQr] = useState('');
  const [full, setFull] = useState(false);

  useEffect(() => {
    if (!store) return;
    let current = true;
    void keptMyCard(store).then((kept) => current && kept && setCard(kept));
    void refreshMyCard(store).then((fresh) => current && fresh && setCard(fresh));
    return () => {
      current = false;
    };
  }, [store]);

  // The device's current event, for "Met at ...".
  useEffect(() => {
    if (!store || !eventId) return;
    let current = true;
    void store
      .getRecord('event', eventId)
      .then((e) => current && setEvent(e ? eventName(e) : null));
    return () => {
      current = false;
    };
  }, [store, eventId]);

  const name = `${card.firstName} ${card.lastName}`.trim();
  const note = event && metAt ? `Met at ${event}` : null;
  const text = vCard({
    firstName: card.firstName,
    lastName: card.lastName,
    title: card.jobTitle,
    email: card.email,
    mobile: card.mobile,
    office: card.officePhone,
    linkedin: card.linkedinUrl,
    note,
  });

  useEffect(() => {
    let current = true;
    void QRCode.toString(text, { type: 'svg', margin: 3, errorCorrectionLevel: 'L' }).then(
      (svg) => current && setQr(svg),
      () => current && setQr(''),
    );
    return () => {
      current = false;
    };
  }, [text]);

  const fileName = `${name.replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '') || 'contact'}.vcf`;
  const vcf = () => new Blob([text], { type: 'text/vcard' });

  function download() {
    const url = URL.createObjectURL(vcf());
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.append(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  }

  /** The phone's share sheet (with the .vcf when it can), else the details copied. */
  async function share() {
    const summary = [
      [name, card.jobTitle, 'Waste Robotics'].filter(Boolean).join(', '),
      card.mobile,
      card.email,
      card.linkedinUrl,
    ]
      .filter(Boolean)
      .join('\n');
    try {
      const file = new File([vcf()], fileName, { type: 'text/vcard' });
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ title: name, text: summary, files: [file] });
        return;
      }
      if (navigator.share) {
        await navigator.share({ title: name, text: summary });
        return;
      }
    } catch (error) {
      if ((error as Error).name === 'AbortError') return;
    }
    try {
      await navigator.clipboard.writeText(summary);
      toast('Contact details copied.');
    } catch {
      toast('Sharing is not available here. Use the QR code or the .vcf file.');
    }
  }

  const lines = [
    card.mobile && `Mobile ${card.mobile}`,
    card.officePhone && `Office ${card.officePhone}`,
    card.email,
    card.linkedinUrl,
  ].filter(Boolean) as string[];
  const qrImage = (className: string) => (
    <div
      className={className}
      role="img"
      aria-label={`QR code with the contact details of ${name}`}
      dangerouslySetInnerHTML={{ __html: qr }}
    />
  );

  if (full) {
    // Prototype: openCardFull. Tap anywhere to go back to the card.
    return (
      <div
        className="cardfull"
        role="dialog"
        aria-label="My contact QR code"
        tabIndex={0}
        onClick={() => setFull(false)}
        onKeyDown={(e) => (e.key === 'Escape' || e.key === 'Enter') && setFull(false)}
      >
        {qrImage('cf-qr')}
        <div className="cf-name">{name}</div>
        <div className="cf-sub">{[card.jobTitle, 'Waste Robotics'].filter(Boolean).join(', ')}</div>
        <p>Scan with your phone camera to save my contact. Tap anywhere to close.</p>
      </div>
    );
  }

  return (
    <Dialog
      title="My business card"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn left" onClick={onAccount}>
            My account
          </button>
          <button type="button" className="btn" onClick={() => void share()}>
            Share
          </button>
          <button type="button" className="btn" onClick={download}>
            Download .vcf
          </button>
        </>
      }
    >
      <div className="vcard">
        <img className="vcard-logo" src="/logo.png" alt="Waste Robotics" />
        {qrImage('vcard-qr')}
        <div className="vcard-name">{name}</div>
        {card.jobTitle && <div className="vcard-title">{card.jobTitle}</div>}
        <div className="vcard-lines">
          {lines.map((line) => (
            <div key={line}>{line}</div>
          ))}
        </div>
      </div>
      <p className="note vcard-help">
        The prospect points their phone camera at the code and saves your contact in one tap. No
        app, no internet needed.
      </p>
      {event && (
        <label className="chip vcard-met">
          <input type="checkbox" checked={metAt} onChange={(e) => setMetAt(e.target.checked)} />
          Add &ldquo;Met at {event}&rdquo; to the contact
        </label>
      )}
      <button type="button" className="btn primary huge" onClick={() => setFull(true)}>
        Show full screen
      </button>
    </Dialog>
  );
}
